import { useMemo, useState } from "react";

type ClientPaginationResult<T> = {
  query: string;
  setQuery: (value: string) => void;
  page: number;
  setPage: (value: number) => void;
  totalPages: number;
  totalFiltered: number;
  pageItems: T[];
};

export function useClientPagination<T>(
  items: T[],
  pageSize: number,
  filterFn: (item: T, query: string) => boolean,
): ClientPaginationResult<T> {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const filtered = useMemo(
    () => {
      const trimmed = query.trim().toLowerCase();
      if (!trimmed) return items;
      return items.filter((item) => filterFn(item, trimmed));
    },
    [items, query, filterFn],
  );

  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));

  const safePage = Math.min(page, totalPages);
  if (safePage !== page) {
    setPage(safePage);
  }

  const pageItems = useMemo(
    () => filtered.slice((safePage - 1) * pageSize, safePage * pageSize),
    [filtered, safePage, pageSize],
  );

  return { query, setQuery, page: safePage, setPage, totalPages, totalFiltered, pageItems };
}
